/**
 * FilePreviewModal — single global in-page viewer for authed files.
 *
 * Mounted once (main.tsx) and driven by useFilePreviewStore. When a consumer
 * calls openFilePreview({ path }), this fetches the file as a blob through the
 * authed apiClient (the in-memory bearer token can't ride on a plain link, and
 * presigned Minio URLs point at the internal host), then renders it as a popup
 * over the current tab — images inline, PDFs in an iframe, anything else as a
 * download. Replaces the previous new-tab behaviour (openProxiedFile).
 */

import { useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';

import { apiClient } from '@/lib/api/client';
import { useFilePreviewStore } from '@/stores/file-preview-store';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export function FilePreviewModal() {
  const open = useFilePreviewStore((s) => s.open);
  const path = useFilePreviewStore((s) => s.path);
  const title = useFilePreviewStore((s) => s.title);
  const downloadName = useFilePreviewStore((s) => s.downloadName);
  const mimeHint = useFilePreviewStore((s) => s.mimeHint);
  const close = useFilePreviewStore((s) => s.close);

  const [objUrl, setObjUrl] = useState<string | null>(null);
  const [mime, setMime] = useState<string>('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open || !path) return;
    let cancelled = false;
    let created: string | null = null;
    setObjUrl(null);
    setFailed(false);
    setMime(mimeHint ?? '');

    apiClient
      .get(path, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        const blob = res.data as Blob;
        created = URL.createObjectURL(blob);
        setObjUrl(created);
        // Prefer the blob's own Content-Type; fall back to the hint only when
        // the server left it blank ('' is not nullish, so ?? won't catch it).
        setMime(blob.type !== '' ? blob.type : (mimeHint ?? ''));
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [open, path, mimeHint]);

  const isPdf = mime === 'application/pdf';
  const isImage = mime.startsWith('image/');

  const download = () => {
    if (!objUrl) return;
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = downloadName ?? title;
    a.click();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : close())}>
      <DialogContent
        aria-describedby={undefined}
        className="w-[min(95vw,56rem)] max-w-none gap-0 overflow-hidden p-0"
      >
        <DialogHeader className="flex flex-row items-center justify-between space-y-0 border-b border-border px-4 py-3 pr-12 text-left">
          <DialogTitle className="truncate text-base">{title}</DialogTitle>
          {objUrl && (
            <Button variant="ghost" size="sm" onClick={download} aria-label="Download file">
              <Download className="mr-1 h-4 w-4" aria-hidden="true" />
              Download
            </Button>
          )}
        </DialogHeader>

        <div className="max-h-[80vh] overflow-auto bg-muted/30">
          {failed ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              Couldn’t load this file. It may have been moved or you may not have access.
            </div>
          ) : !objUrl ? (
            <div className="flex items-center justify-center p-16 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" aria-label="Loading file" />
            </div>
          ) : isPdf ? (
            <iframe
              src={objUrl}
              title={title}
              className="h-[80vh] w-full border-0"
              aria-label={`${title} (PDF)`}
            />
          ) : isImage ? (
            <div className="flex justify-center p-4">
              <img
                src={objUrl}
                alt={title}
                className="max-h-[78vh] max-w-full rounded object-contain"
              />
            </div>
          ) : (
            <div className="p-10 text-center">
              <Download className="mx-auto mb-2 h-6 w-6 text-muted-foreground" aria-hidden="true" />
              <p className="mb-3 text-sm text-muted-foreground">
                This file type can’t be previewed in the browser.
              </p>
              <Button size="sm" variant="outline" onClick={download}>
                Download file
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
